/**
 * Beta Feature Registry
 *
 * Shared beta feature definitions and access math.
 *
 * Primary responsibilities:
 * - Register beta features in one typed list
 * - Resolve profile-level and rollout-level feature access
 *
 * Dependencies:
 * - Super admin email helper
 * - `@/lib/crew/your-crew-beta-registry-blurb` → CREW_YOUR_CREW_BETA_REGISTRY_BLURB
 */

import { CREW_YOUR_CREW_BETA_REGISTRY_BLURB } from '@/lib/crew/your-crew-beta-registry-blurb'
import { isSuperAdmin } from '@/lib/utils'

export const LOSI_APPS_BETA_FEATURE_KEY = 'losi_apps'
export const SPACE_DESIGNS_BETA_FEATURE_KEY = 'space_designs'
export const CANVAS_BROWSER_BETA_FEATURE_KEY = 'canvas_browser'
export const YOUR_CREW_BETA_FEATURE_KEY = 'your_crew'
export const CHAT_REALTIME_BETA_FEATURE_KEY = 'chat_realtime'

export type BetaFeatureDefinition = {
  adminAlwaysEnabled: boolean
  defaultEnabled: boolean
  defaultRolloutPercentage: number
  description: string
  key: string
  name: string
  productArea: string
}

export type BetaFeatureAccessProfile = {
  email?: string | null
  is_platform_admin?: boolean | null
}

export type BetaFeatureRolloutState = {
  enabled: boolean
  rolloutPercentage: number
}

export const BETA_FEATURES = [
  {
    adminAlwaysEnabled: true,
    defaultEnabled: false,
    defaultRolloutPercentage: 0,
    description: 'AI-native generated app surfaces, actions, records, runs, and app editing.',
    key: LOSI_APPS_BETA_FEATURE_KEY,
    name: 'Losi Apps',
    productArea: 'Apps'
  },
  {
    adminAlwaysEnabled: true,
    defaultEnabled: false,
    defaultRolloutPercentage: 0,
    description:
      'Canvas-style layouts, editable design imports, and Space design collaboration.',
    key: SPACE_DESIGNS_BETA_FEATURE_KEY,
    name: 'Space Designs',
    productArea: 'Spaces'
  },
  {
    adminAlwaysEnabled: true,
    defaultEnabled: false,
    defaultRolloutPercentage: 0,
    description:
      'Live cloud browser in the canvas, with Losi able to navigate, click, and type in it.',
    key: CANVAS_BROWSER_BETA_FEATURE_KEY,
    name: 'Canvas Browser',
    productArea: 'Chat'
  },
  {
    adminAlwaysEnabled: true,
    defaultEnabled: false,
    defaultRolloutPercentage: 0,
    description: CREW_YOUR_CREW_BETA_REGISTRY_BLURB,
    key: YOUR_CREW_BETA_FEATURE_KEY,
    name: 'Your Crew',
    productArea: 'Crew'
  },
  {
    // Kept in the registry for historical DB rows / admin UI, but chat realtime
    // is GA — session mint no longer checks this key.
    adminAlwaysEnabled: true,
    defaultEnabled: true,
    defaultRolloutPercentage: 100,
    description:
      'Losi Inference realtime voice in chat (mic, live transcripts, tools). Generally available — session API does not gate on this flag.',
    key: CHAT_REALTIME_BETA_FEATURE_KEY,
    name: 'Chat Realtime',
    productArea: 'Chat'
  }
] as const

const BETA_FEATURE_KEYS = new Set<string>(
  BETA_FEATURES.map((feature) => feature.key)
)

export type BetaFeatureKey = (typeof BETA_FEATURES)[number]['key']

// --------------

export function isBetaFeatureKey(value: string): value is BetaFeatureKey {
  return BETA_FEATURE_KEYS.has(value)
}

// --------------

export function getBetaFeatureDefinition(featureKey: BetaFeatureKey) {
  return BETA_FEATURES.find((feature) => feature.key === featureKey)
}

// --------------

export function requireBetaFeatureDefinition(featureKey: BetaFeatureKey) {
  const definition = getBetaFeatureDefinition(featureKey)

  if (!definition) {
    throw new Error(`Unknown beta feature: ${featureKey}`)
  }

  return definition
}

// --------------

export function hasBetaFeatureAdminAccess(
  profile: BetaFeatureAccessProfile | null | undefined
) {
  return Boolean(
    profile?.is_platform_admin === true ||
      isSuperAdmin(profile?.email)
  )
}

// --------------

export function canUseBetaFeatureFromProfile(
  featureKey: BetaFeatureKey,
  profile: BetaFeatureAccessProfile | null | undefined
) {
  const definition = requireBetaFeatureDefinition(featureKey)

  if (definition.adminAlwaysEnabled && hasBetaFeatureAdminAccess(profile)) {
    return true
  }

  return definition.defaultEnabled
}

// --------------

export function getBetaFeatureRolloutBucket(
  userId: string,
  featureKey: BetaFeatureKey
) {
  const input = `${featureKey}:${userId}`
  let hash = 0

  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 31 + input.charCodeAt(index)) >>> 0
  }

  return hash % 100
}

// --------------

export function isUserInBetaFeatureRollout(params: {
  featureKey: BetaFeatureKey
  rolloutPercentage: number
  userId: string
}) {
  const percentage = Math.max(0, Math.min(100, params.rolloutPercentage))

  if (percentage >= 100) {
    return true
  }

  if (percentage <= 0) {
    return false
  }

  return getBetaFeatureRolloutBucket(params.userId, params.featureKey) < percentage
}

// --------------

export function resolveBetaFeatureAccess(params: {
  featureKey: BetaFeatureKey
  overrideEnabled?: boolean | null
  profile?: BetaFeatureAccessProfile | null
  rollout?: BetaFeatureRolloutState | null
  userId?: string | null
}) {
  const definition = requireBetaFeatureDefinition(params.featureKey)

  if (
    definition.adminAlwaysEnabled &&
    hasBetaFeatureAdminAccess(params.profile)
  ) {
    return true
  }

  if (typeof params.overrideEnabled === 'boolean') {
    return params.overrideEnabled
  }

  const rollout = params.rollout

  if (!rollout?.enabled) {
    return definition.defaultEnabled
  }

  if (!params.userId) {
    return definition.defaultEnabled
  }

  return isUserInBetaFeatureRollout({
    featureKey: params.featureKey,
    rolloutPercentage: rollout.rolloutPercentage,
    userId: params.userId
  })
}
